// ========================================================================================================
// NonSpotTenderCarrierInvitePage.jsx
// --------------------------------------------------------------------------------------------------------
// Carrier realm page for Non-Spot tenders.
//
// Layout:
//   - MTabsInpage with four sections: Non-Spot Tenders (overview), Invited, In Quotation, Finished.
//   - Overview section renders a single MTable with Non-Spot tenders.
//       entityName = "TenderPhaseCarriers/tenders"
//   - Invited / In Quotation / Finished sections render:
//       1) A DynamicSelectBox to pick a Non-Spot tender for this carrier.
//       2) A MTable with phases for the selected tender, filtered by the logged carrier and tab status.
//          entityName = "TenderPhaseCarriers/carrier"
//
// Navigation:
//   - View on a tender row (overview tab) opens TenderConfigCrud in tender mode (IsPhaseMode = false).
//   - View on a phase row opens TenderConfigCrud in phase mode (IsPhaseMode = true).
//
// Session:
//   - Uses M_State to persist "menu" (selected section), "refresh", and per-status selection per dynamic tab.
//   - Each MTable receives a manualId derived from the tab id to isolate state per tab and per section.
//
// Notes:
//   - Page is again allowed to control the global loading bar when restoring session.
//   - Child table in each status tab is only rendered once a tender is selected in the DynamicSelectBox.
// ========================================================================================================

import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef, // needed to detect "return from submit"
} from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'

import {
  LoadingContext,
  M_StateActions,
  MTabsInpage,
  NavigateContext,
  MTable,
} from 'src/M_Components'

import { hasPermission } from 'src/components/utils/permissionsHelper'

// NOTE: adjust this import path for DynamicSelectBox according to your project structure
import DynamicSelectBox from 'src/components/Forms/DynamicSelectBox'

import 'src/pages/tabsInpage.css'

const CRUD_ROUTE = '/CarrierInviteViewBid'

export default function NonSpotTenderCarrierInvitePage({
  roles,
  onCreateTab,
  handleTabClose, // compatibility only
  toastMessages,
}) {
  const ctx = useContext(LoadingContext)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const matches = useMediaQuery('(min-width:900px)')
  const M_State = useSelector((s) => s.StateKeyValue)
  const location = useLocation()
  const { renameTab } = useContext(NavigateContext)

  const selectedTab = useSelector((s) =>
    s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  )

  // Menu: 1 = Non-Spot Tenders, 2 = Invited, 3 = In Quotation, 4 = Finished
  const [menu, setMenu] = useState(1)
  const [refresh, setRefresh] = useState(false)
  const [id, setId] = useState(() =>
    M_State.find((obj) => obj.key === 'selectedTab')?.value
  )

  // Per-status selection persisted per dynamic tab:
  //   invitedSelection  -> tab 2 (Invited)
  //   inQuotationSelection -> tab 3 (In Quotation)
  //   finishedSelection  -> tab 4 (Finished)
  // Each object is either null or { inviteId, tenderId }.
  const [invitedSelection, setInvitedSelection] = useState(null)
  const [inQuotationSelection, setInQuotationSelection] = useState(null)
  const [finishedSelection, setFinishedSelection] = useState(null)

  // Track the previous pathname so we can detect when we come back from the submit page.
  // This lets us clear stale persisted selections (which would otherwise trigger a GET-by-id in DynamicSelectBox).
  const prevPathRef = useRef(location.pathname)

  // --------------------------------------------------------------------------------------
  // Carrier resolution
  // --------------------------------------------------------------------------------------

  const carrierIdFromState = M_State.find((o) => o.key === 'CarrierId')?.value

  const carrierSession = useMemo(() => {
    try {
      const raw = localStorage.getItem('app.auth.carrier.session')
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }, [])

  const carrierId =
    carrierIdFromState ?? ctx.carrierId ?? carrierSession?.CarrierId ?? null

  // --------------------------------------------------------------------------------------
  // Filters
  // --------------------------------------------------------------------------------------

  const baseParentStatus = 'Inactive eq false'

  const parentFilter =
    carrierId != null
      ? `CarrierId eq ${Number(carrierId)} and ${baseParentStatus}`
      : baseParentStatus

  const phaseInvitedFilter =
    'Inactive eq false and State eq 1 and HasSubmitted eq false'

  const phaseInQuotationFilter =
    'Inactive eq false and State eq 1 and HasSubmitted eq true'

  const phaseFinishedFilter = 'Inactive eq false and State eq 3'

  const rolePermissions = roles?.map((r) => r.Value) ?? []

  // --------------------------------------------------------------------------------------
  // Navigation helpers
  // --------------------------------------------------------------------------------------

  const openTenderFromTender = useCallback(
    (row) => {
      if (!row) {
        toastMessages && toastMessages('error', '', t('SelectedLine'))
        return
      }

      dispatch(M_StateActions.addM_State({ key: 'operation', value: 'View' }))

      const tenderLine = {
        Id: row.TenderId ?? row.Id,
        Name: row.Tender ?? row.Name ?? '',
      }

      dispatch(
        M_StateActions.addM_State({
          key: 'TenderLine',
          value: tenderLine,
        })
      )

      dispatch(
        M_StateActions.addM_State({
          key: 'IsPhaseMode',
          value: false,
        })
      )

      dispatch(M_StateActions.removeM_State({ key: 'TenderPhaseLine' }))

      // Menu mapping: only status tabs (2: Invited, 3: In Quotation) drive create/edit.
      let bidCrudMode = 'view'
      if (menu === 2) bidCrudMode = 'create'
      else if (menu === 3) bidCrudMode = 'edit'

      dispatch(
        M_StateActions.addM_State({
          key: 'CarrierBidOrigin',
          value: {
            scope: 'tender',
            crudMode: bidCrudMode,
          },
        })
      )

      const baseLabel = t('LabelViewTender')
      const extra = tenderLine.Name || ''
      const tabLabel = extra ? `${baseLabel} "${extra}"` : baseLabel

      onCreateTab && onCreateTab(CRUD_ROUTE, tabLabel)
    },
    [dispatch, menu, onCreateTab, toastMessages, t]
  )

  const openTenderFromPhase = useCallback(
    (row) => {
      if (!row) {
        toastMessages && toastMessages('error', '', t('SelectedLine'))
        return
      }

      dispatch(M_StateActions.addM_State({ key: 'operation', value: 'View' }))

      const tenderLine = {
        Id: row.TenderId ?? row.Tender?.Id ?? row.Id,
        Name: row.Tender ?? row.TenderName ?? row.Name ?? '',
      }

      const phaseLine = {
        Id: row.TenderPhaseId ?? row.Id,
        Name: row.TenderPhase ?? row.Name ?? '',
      }

      dispatch(
        M_StateActions.addM_State({
          key: 'TenderLine',
          value: tenderLine,
        })
      )

      dispatch(
        M_StateActions.addM_State({
          key: 'IsPhaseMode',
          value: true,
        })
      )

      dispatch(
        M_StateActions.addM_State({
          key: 'TenderPhaseLine',
          value: phaseLine,
        })
      )

      // Menu mapping: only status tabs (2: Invited, 3: In Quotation) drive create/edit.
      let bidCrudMode = 'view'
      if (menu === 2) bidCrudMode = 'create'
      else if (menu === 3) bidCrudMode = 'edit'

      dispatch(
        M_StateActions.addM_State({
          key: 'CarrierBidOrigin',
          value: {
            scope: 'phase',
            crudMode: bidCrudMode,
          },
        })
      )

      const baseLabel =
        t('LabelViewTenderPhase') || t('LabelViewTender') || 'Tender Phase'
      const extra = phaseLine.Name || tenderLine.Name || ''
      const tabLabel = extra ? `${baseLabel} "${extra}"` : baseLabel

      onCreateTab && onCreateTab(CRUD_ROUTE, tabLabel)
    },
    [dispatch, menu, onCreateTab, toastMessages, t]
  )

  const parentReplaceFunctions = useMemo(
    () => new Map([['View', (row) => openTenderFromTender(row)]]),
    [openTenderFromTender]
  )

  const childReplaceFunctions = useMemo(
    () => new Map([['View', (row) => openTenderFromPhase(row)]]),
    [openTenderFromPhase]
  )

  const removeContextItems = useMemo(
    () => ['Print', 'Copy', 'Create', 'Edit', 'Delete'],
    []
  )

  // --------------------------------------------------------------------------------------
  // Session helpers (menu / refresh / per-status selection per tab)
  // --------------------------------------------------------------------------------------

  const upsertSession = (tabId, kv) => {
    if (!tabId) return
    const current = M_State.find((o) => o.key === tabId)?.value || []
    const filtered = current.filter((e) => e.key !== kv.key)
    dispatch(
      M_StateActions.addM_State({
        key: tabId,
        value: [...filtered, kv],
      })
    )
  }

  // Page-level session restore now also controls the global loading bar again.
  // It now also restores per-status selection for Invited / In Quotation / Finished.
  const getSessionStorageData = (sessionID) => {
    if (!sessionID) return

    ctx.setIsLoad(true)

    const session = M_State.find((obj) => obj.key === sessionID)?.value
    if (session) {
      const m = session.find((x) => x.key === 'menu')?.value
      const ref = session.find((x) => x.key === 'refresh')?.value
      const invitedSel = session.find((x) => x.key === 'ns-invited')?.value
      const inQuotationSel = session.find((x) => x.key === 'ns-inQuotation')
        ?.value
      const finishedSel = session.find((x) => x.key === 'ns-finished')?.value

      setMenu(typeof m === 'number' ? m : 1)
      if (ref !== undefined) setRefresh(ref)

      // Safely restore stored selections (or null if not present).
      setInvitedSelection(invitedSel || null)
      setInQuotationSelection(inQuotationSel || null)
      setFinishedSelection(finishedSel || null)
    } else {
      setMenu(1)
      setInvitedSelection(null)
      setInQuotationSelection(null)
      setFinishedSelection(null)
    }

    ctx.setIsLoad(false)
  }

  // --------------------------------------------------------------------------------------
  // Effects
  // --------------------------------------------------------------------------------------

  useEffect(() => {
    if (!selectedTab || !renameTab) return
    renameTab(String(selectedTab), t('NonSpotTenders'))
  }, [t, selectedTab, renameTab])

  // When returning from the submit page route, clear persisted selections.
  // This prevents DynamicSelectBox from attempting to resolve a stale valueId and triggering a 404.
  useEffect(() => {
    const prev = prevPathRef.current
    const curr = location.pathname

    const cameFromSubmit =
      typeof prev === 'string' &&
      prev.startsWith(CRUD_ROUTE) &&
      typeof curr === 'string' &&
      !curr.startsWith(CRUD_ROUTE)

    if (cameFromSubmit) {
      setInvitedSelection(null)
      setInQuotationSelection(null)
      setFinishedSelection(null)
    }

    prevPathRef.current = curr
  }, [location.pathname])

  useEffect(() => {
    if (!id) return
    getSessionStorageData(id)
    setRefresh((r) => !r)
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value
    if (newId && newId !== id) {
      setId(newId)
      getSessionStorageData(newId)
    }
  }, [M_State]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return
    upsertSession(id, { key: 'menu', value: menu })
  }, [id, menu]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return
    upsertSession(id, { key: 'refresh', value: refresh })
  }, [id, refresh]) // eslint-disable-line react-hooks/exhaustive-deps

  // Persist per-status selection in session whenever it changes.
  useEffect(() => {
    if (!id) return
    upsertSession(id, { key: 'ns-invited', value: invitedSelection })
  }, [id, invitedSelection]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return
    upsertSession(id, { key: 'ns-inQuotation', value: inQuotationSelection })
  }, [id, inQuotationSelection]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return
    upsertSession(id, { key: 'ns-finished', value: finishedSelection })
  }, [id, finishedSelection]) // eslint-disable-line react-hooks/exhaustive-deps

  // --------------------------------------------------------------------------------------
  // manualIds
  // --------------------------------------------------------------------------------------

  // Overview tab (tenders list only)
  const overviewParentId = useMemo(
    () => (id ? `${id}-ns-overview-parent` : undefined),
    [id]
  )

  // Status tabs use only child manual ids (phases table)
  const invitedChildId = useMemo(() => (id ? `${id}-ns-inv-child` : undefined), [
    id,
  ])

  const inQuotationChildId = useMemo(
    () => (id ? `${id}-ns-quot-child` : undefined),
    [id]
  )

  const finishedChildId = useMemo(() => (id ? `${id}-ns-fin-child` : undefined), [
    id,
  ])

  // --------------------------------------------------------------------------------------
  // Tabs config
  // --------------------------------------------------------------------------------------

  const allowedTabs = () => {
    const tabArray = []

    if (
      hasPermission(roles, 'Tenders', 'List') ||
      hasPermission(roles, 'Tenders', 'Admin')
    ) {
      tabArray.push(
        {
          id: 1,
          labelTab: t('Non-Spot Tenders'),
          iconTab: <i className="pi pi-list" style={{ fontSize: '1.5rem' }} />,
        },
        {
          id: 2,
          labelTab: t('Non-Spot Tenders Invited'),
          iconTab: <i className="pi pi-envelope" style={{ fontSize: '1.5rem' }} />,
        },
        {
          id: 3,
          labelTab: t('Non-Spot Tenders In Quotation'),
          iconTab: (
            <i className="pi pi-chart-line" style={{ fontSize: '1.5rem' }} />
          ),
        },
        {
          id: 4,
          labelTab: t('Non-Spot Tenders Finished'),
          iconTab: (
            <i className="pi pi-check-square" style={{ fontSize: '1.5rem' }} />
          ),
        }
      )
    }

    return tabArray
  }

  // --------------------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------------------

  return (
    <div className="tabsInpage divGlobalSettings">
      <div
        className="tabsInpage__title titleSettings"
        style={{ color: ctx.secondaryColor }}
      >
        {t('NonSpotTenders')}
      </div>

      <div className="tabsInpage__body">
        <div className="tabsInpage__sidebar">
          <MTabsInpage
            type={matches ? 'column' : 'tabs'}
            renderTabs={allowedTabs()}
            firstTabOpen={menu}
            onGetTab={(obj, obj2) => {
              if (matches) setMenu(obj.id)
              else setMenu(obj2?.id ?? obj.id)
            }}
            fixedArea={false}
          />
        </div>

        <div className="tabsInpage__content settingsPageMainDiv">
          {/* Overview tab with tenders list only */}
          {menu === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <MTable
                filtersOnTable
                entityName="TenderPhaseCarriers/tenders"
                manualId={overviewParentId}
                rolePermissions={rolePermissions}
                useSessionStorage={false}
                FILTER={parentFilter}
                maxRows={20}
                stopSaveRows={false}
                removeContextItems={removeContextItems}
                replaceFunctions={parentReplaceFunctions}
                selectSingle
              />
            </div>
          )}

          {/* Invited phases controlled by DynamicSelectBox */}
          {menu === 2 && (
            <NonSpotSectionTables
              childManualId={invitedChildId}
              parentFilter={parentFilter}
              phaseStatusFilter={phaseInvitedFilter}
              carrierId={carrierId}
              rolePermissions={rolePermissions}
              removeContextItems={removeContextItems}
              childReplaceFunctions={childReplaceFunctions}
              storedInviteId={invitedSelection?.inviteId ?? null}
              storedTenderId={invitedSelection?.tenderId ?? null}
              onPersistSelection={setInvitedSelection}
            />
          )}

          {/* In Quotation phases controlled by DynamicSelectBox */}
          {menu === 3 && (
            <NonSpotSectionTables
              childManualId={inQuotationChildId}
              parentFilter={parentFilter}
              phaseStatusFilter={phaseInQuotationFilter}
              carrierId={carrierId}
              rolePermissions={rolePermissions}
              removeContextItems={removeContextItems}
              childReplaceFunctions={childReplaceFunctions}
              storedInviteId={inQuotationSelection?.inviteId ?? null}
              storedTenderId={inQuotationSelection?.tenderId ?? null}
              onPersistSelection={setInQuotationSelection}
            />
          )}

          {/* Finished phases controlled by DynamicSelectBox */}
          {menu === 4 && (
            <NonSpotSectionTables
              childManualId={finishedChildId}
              parentFilter={parentFilter}
              phaseStatusFilter={phaseFinishedFilter}
              carrierId={carrierId}
              rolePermissions={rolePermissions}
              removeContextItems={removeContextItems}
              childReplaceFunctions={childReplaceFunctions}
              storedInviteId={finishedSelection?.inviteId ?? null}
              storedTenderId={finishedSelection?.tenderId ?? null}
              onPersistSelection={setFinishedSelection}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ========================================================================================================
// Section component (DynamicSelectBox + phases MTable)
// ========================================================================================================

function NonSpotSectionTables({
  childManualId, // kept only for compatibility in case manual ids are used externally
  parentFilter,
  phaseStatusFilter,
  carrierId,
  rolePermissions,
  removeContextItems,
  childReplaceFunctions,
  storedInviteId, // persisted relation id from TenderPhaseCarriers/tenders
  storedTenderId, // persisted TenderId used to rebuild the filter
  onPersistSelection, // callback to persist current selection in parent/session
}) {
  const { t } = useTranslation()

  // Relation Id (TenderPhaseCarriers/tenders row Id) currently selected in the combo
  const [selectedInviteId, setSelectedInviteId] = useState(
    storedInviteId ?? null
  )

  // Real TenderId for the selected tender
  const [effectiveTenderId, setEffectiveTenderId] = useState(
    storedTenderId ?? null
  )

  // Final filter for the phases MTable
  const [phaseFilter, setPhaseFilter] = useState(null)

  const resetSelection = useCallback(() => {
    setSelectedInviteId(null)
    setEffectiveTenderId(null)
    setPhaseFilter(null)
    onPersistSelection?.(null)
  }, [onPersistSelection])

  // Rehydrate local state from stored selection whenever it changes.
  useEffect(() => {
    if (!carrierId || !storedTenderId || !storedInviteId) {
      setSelectedInviteId(null)
      setEffectiveTenderId(null)
      setPhaseFilter(null)
      return
    }

    setSelectedInviteId(storedInviteId)
    setEffectiveTenderId(storedTenderId)

    const filters = [
      `TenderId eq ${storedTenderId}`,
      `CarrierId eq ${Number(carrierId)}`,
      phaseStatusFilter,
    ]

    setPhaseFilter(filters.join(' and '))
  }, [carrierId, storedTenderId, storedInviteId, phaseStatusFilter])

  // When the user selects a tender in the combo
  const handleTenderChange = useCallback(
    (option) => {
      if (!option || !carrierId) {
        resetSelection()
        return
      }

      // The DynamicSelectBox exposes the full row in option.raw
      const raw = option.raw ?? option
      const tenderId = Number(raw?.TenderId ?? raw?.tenderId ?? raw?.Id)
      const inviteId = Number(option.value)

      if (
        !Number.isFinite(tenderId) ||
        tenderId <= 0 ||
        !Number.isFinite(inviteId) ||
        inviteId <= 0
      ) {
        resetSelection()
        return
      }

      setSelectedInviteId(inviteId)
      setEffectiveTenderId(tenderId)

      const filters = [
        `TenderId eq ${tenderId}`,
        `CarrierId eq ${Number(carrierId)}`,
        phaseStatusFilter,
      ]

      setPhaseFilter(filters.join(' and '))

      // Persist both relation id and tender id so we can restore later.
      onPersistSelection?.({ inviteId, tenderId })
    },
    [carrierId, phaseStatusFilter, resetSelection, onPersistSelection]
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* DynamicSelectBox: lists only tenders for this carrier, nothing else */}
      <div style={{ maxWidth: 400 }}>
        <DynamicSelectBox
          label={t('SelectTender')}
          tableName="TenderPhaseCarriers/tenders"
          // IMPORTANT: groupBy should match the actual property name used for searching (e.g. "Name" or "Tender").
          groupBy="Name"
          // We control valueId so the combo can re-select when coming back to this tab.
          valueId={selectedInviteId}
          onChange={handleTenderChange}
          staticFilter={parentFilter}
          required={false}
        />
      </div>

      {/* Only mount the MTable when we already have a correct filter.
          This avoids initial calls like "TenderId eq 0". */}
      {effectiveTenderId && phaseFilter && (
        <MTable
          key={`NonSpotPhases-${effectiveTenderId}-${phaseStatusFilter}`}
          filtersOnTable
          entityName="TenderPhaseCarriers/carrier"
          manualId={childManualId}
          rolePermissions={rolePermissions}
          useSessionStorage={false}
          FILTER={phaseFilter}
          maxRows={20}
          stopSaveRows={false}
          removeContextItems={removeContextItems}
          replaceFunctions={childReplaceFunctions}
          selectSingle
        />
      )}
    </div>
  )
}
